import { useMemo } from 'react'
import type { UseTranslationOptions } from 'react-i18next'

import type { KeyPrefix, Namespace, TOptions } from 'i18next'

import { tFunctionImplementation } from './tFunctionImplementation'
import type { UseTranslationResponseWithConditionalI18n } from './types'

/**
 *
 */
export function useTranslationImplementation<
  N extends Namespace,
  TKPrefix extends KeyPrefix<N>,
>(
  language: string | readonly string[],
  ns?: N | Readonly<N>,
  options?: UseTranslationOptions<TKPrefix>,
  postProcessFn?: (text: string) => string,
): UseTranslationResponseWithConditionalI18n<N, TKPrefix> {
  const t: (tKey: string, tOptions: TOptions) => string = useMemo(
    () =>
      options?.i18n
        ? options?.i18n?.getFixedT<N>(language, ns)
        : (tKey: string, tOptions: TOptions) => {
            tKey = tFunctionImplementation(tKey, tOptions)

            /** Apply pseudo localization or other postprocess. For dev purposes only */
            return postProcessFn ? postProcessFn(tKey) : tKey
          },
    [language, ns, options?.i18n, postProcessFn],
  )

  const i18n = options?.i18n
  const ready = !!options?.i18n

  const ret = Object.create([t, i18n, ready])
  ret.t = t
  ret.i18n = i18n
  ret.ready = ready

  return ret
}
